import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEXT_FIELDS = [
    { key: 'site_title', label: 'Site Title', type: 'text' },
    { key: 'tagline', label: 'Tagline', type: 'text' },
    { key: 'hero_heading', label: 'Hero Heading (Landing Page)', type: 'text' },
    { key: 'hero_subheading', label: 'Hero Sub-heading', type: 'textarea' },
    { key: 'about_heading', label: 'About Section Heading', type: 'text' },
    { key: 'about_body', label: 'About Section Body', type: 'textarea' },
    { key: 'contact_email', label: 'Contact Email', type: 'email' },
    { key: 'email_from_name', label: 'Transactional Email — Display Name (shown as sender)', type: 'text' },
    { key: 'email_reply_to', label: 'Transactional Email — Reply-To address', type: 'email' },
    { key: 'seeker_upsell_price_eur', label: 'Seeker upsell price (€) — the "+€X unlock a 1-month Seeker" offer on free events & library items', type: 'number', step: '0.5', min: '0' }
];

const FOOTER_FIELDS = [
    { key: 'footer_tagline', label: 'Footer Tagline (under site name)', type: 'textarea' },
    { key: 'footer_text', label: 'Footer Bottom Text (copyright line)', type: 'text' }
];

function LinkRepeater({ items, onChange, prefix }) {
    const update = (idx, key, val) => {
        const next = [...items];
        next[idx] = { ...next[idx], [key]: val };
        onChange(next);
    };
    const add = () => onChange([...items, { label: '', href: '' }]);
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

    return (
        <div className="space-y-3" data-testid={`${prefix}-repeater`}>
            {items.map((it, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-center">
                    <input
                        type="text"
                        placeholder="Label"
                        value={it.label || ''}
                        onChange={(e) => update(i, 'label', e.target.value)}
                        className="text-sm"
                        data-testid={`${prefix}-label-${i}`}
                    />
                    <input
                        type="text"
                        placeholder="https://… or /events"
                        value={it.href || ''}
                        onChange={(e) => update(i, 'href', e.target.value)}
                        className="text-sm"
                        data-testid={`${prefix}-href-${i}`}
                    />
                    <button
                        type="button"
                        onClick={() => remove(i)}
                        className="p-2 hover:bg-red-50 rounded"
                        title="Remove"
                        data-testid={`${prefix}-remove-${i}`}
                    >
                        <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={add}
                className="btn-secondary text-xs flex items-center gap-2"
                data-testid={`${prefix}-add`}
            >
                <Plus className="w-4 h-4" /> Add link
            </button>
        </div>
    );
}

export default function SiteSettingsPanel() {
    const [settings, setSettings] = useState({});
    const [pages, setPages] = useState([]);
    const [pillars, setPillars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const [sRes, pRes, plRes] = await Promise.all([
                axios.get(`${API_URL}/api/site-settings`),
                axios.get(`${API_URL}/api/pages`, { withCredentials: true }),
                axios.get(`${API_URL}/api/pillars`)
            ]);
            setSettings(sRes.data || {});
            setPages(pRes.data || []);
            setPillars(plRes.data || []);
        } catch (err) {
            toast.error('Failed to load site settings');
        } finally {
            setLoading(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {};
            [...TEXT_FIELDS, ...FOOTER_FIELDS].forEach(f => { payload[f.key] = settings[f.key] ?? ''; });
            payload.footer_links = (settings.footer_links || []).filter(l => l.label && l.href);
            payload.footer_social = (settings.footer_social || []).filter(l => l.label && l.href);
            payload.home_page_slug = settings.home_page_slug || '';
            payload.public_nav_show_events = settings.public_nav_show_events !== false;
            payload.public_nav_show_library = settings.public_nav_show_library !== false;
            payload.public_nav_show_membership = settings.public_nav_show_membership !== false;
            payload.public_nav_show_pillars = settings.public_nav_show_pillars !== false;
            payload.pillars_page_heading = settings.pillars_page_heading || '';
            payload.pillars_page_intro = settings.pillars_page_intro || '';
            payload.featured_pillar_override_id = settings.featured_pillar_override_id || '';
            const { data } = await axios.put(`${API_URL}/api/site-settings`, payload, { withCredentials: true });
            setSettings(data);
            window.dispatchEvent(new CustomEvent('site-settings-changed'));
            toast.success('Site settings saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#5B2D8E] mx-auto" /></div>;
    }

    return (
        <form onSubmit={save} className="bg-white border border-[#E5E0D8] rounded-sm p-8 space-y-8" data-testid="site-settings-form">
            <div>
                <p className="overline mb-2">Content</p>
                <h2 className="font-serif text-3xl text-[#1A1A2E]">Site Settings</h2>
                <p className="text-sm text-[#4A4A68] mt-2">
                    These texts power the landing page, navbar, and footer site-wide.
                </p>
            </div>

            {TEXT_FIELDS.map(f => (
                <div key={f.key}>
                    <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">
                        {f.label}
                    </label>
                    {f.type === 'textarea' ? (
                        <textarea
                            rows={4}
                            value={settings[f.key] || ''}
                            onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                            className="w-full resize-none"
                            data-testid={`setting-${f.key}`}
                        />
                    ) : (
                        <input
                            type={f.type}
                            step={f.step}
                            min={f.min}
                            value={settings[f.key] ?? ''}
                            onChange={(e) => setSettings({ ...settings, [f.key]: f.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value })}
                            className="w-full"
                            data-testid={`setting-${f.key}`}
                        />
                    )}
                </div>
            ))}

            <div className="border-t border-[#E5E0D8] pt-8">
                <h3 className="font-serif text-2xl text-[#1A1A2E] mb-2">Navigation & Home</h3>
                <p className="text-sm text-[#4A4A68] mb-6">Control what shows in the public (logged-out) navbar and which page is your homepage.</p>

                <div className="mb-6">
                    <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">
                        Homepage
                    </label>
                    <select
                        value={settings.home_page_slug || ''}
                        onChange={(e) => setSettings({ ...settings, home_page_slug: e.target.value })}
                        className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                        data-testid="setting-home-page"
                    >
                        <option value="">Default landing page (hero + about + memberships)</option>
                        {pages.filter(p => p.is_published !== false).map(p => (
                            <option key={p.id} value={p.slug}>{p.title} (/p/{p.slug})</option>
                        ))}
                    </select>
                    <p className="text-xs text-[#4A4A68] mt-1">If a custom page is selected, "/" will redirect to that page.</p>
                </div>

                <div className="space-y-3">
                    <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">
                        Public Navbar (for non-logged-in visitors)
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={settings.public_nav_show_events !== false}
                            onChange={(e) => setSettings({ ...settings, public_nav_show_events: e.target.checked })}
                            data-testid="setting-public-nav-events"
                        />
                        <span className="text-sm text-[#1A1A2E]">Show "Events" link</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={settings.public_nav_show_library !== false}
                            onChange={(e) => setSettings({ ...settings, public_nav_show_library: e.target.checked })}
                            data-testid="setting-public-nav-library"
                        />
                        <span className="text-sm text-[#1A1A2E]">Show "Library" link</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={settings.public_nav_show_membership !== false}
                            onChange={(e) => setSettings({ ...settings, public_nav_show_membership: e.target.checked })}
                            data-testid="setting-public-nav-membership"
                        />
                        <span className="text-sm text-[#1A1A2E]">Show "Membership" link</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={settings.public_nav_show_pillars !== false}
                            onChange={(e) => setSettings({ ...settings, public_nav_show_pillars: e.target.checked })}
                            data-testid="setting-public-nav-pillars"
                        />
                        <span className="text-sm text-[#1A1A2E]">Show "Pillars" link (Mirror Circle Partnership)</span>
                    </label>
                    <p className="text-xs text-[#4A4A68] mt-2">Forum, Dashboard, Connections always require login. Custom pages can be scoped per-page in the Pages tab.</p>
                </div>

                <div className="border-t border-[#E5E0D8] pt-8 mt-8">
                    <h3 className="font-serif text-2xl text-[#1A1A2E] mb-2">Pillars Page</h3>
                    <p className="text-sm text-[#4A4A68] mb-4">Heading and intro shown at the top of the public Pillars directory.</p>
                    <div className="mb-4">
                        <label className="block text-sm uppercase tracking-widest text-[#4A4A68] mb-2">Heading</label>
                        <input
                            type="text"
                            value={settings.pillars_page_heading || ''}
                            onChange={(e) => setSettings({ ...settings, pillars_page_heading: e.target.value })}
                            className="w-full"
                            data-testid="setting-pillars-heading"
                        />
                    </div>
                    <div>
                        <label className="block text-sm uppercase tracking-widest text-[#4A4A68] mb-2">Intro</label>
                        <textarea
                            rows={3}
                            value={settings.pillars_page_intro || ''}
                            onChange={(e) => setSettings({ ...settings, pillars_page_intro: e.target.value })}
                            className="w-full resize-none"
                            data-testid="setting-pillars-intro"
                        />
                    </div>

                    <div className="mt-6 pt-6 border-t border-[#E5E0D8]">
                        <label className="block text-sm uppercase tracking-widest text-[#4A4A68] mb-2">Featured pillar (homepage)</label>
                        <p className="text-xs text-[#4A4A68] mb-3">Pin a specific pillar to the "Meet this month's pillar" homepage block — e.g. during a launch or campaign. Leave on <em>Auto (monthly rotation)</em> to cycle through every non-admin partner automatically.</p>
                        <select
                            value={settings.featured_pillar_override_id || ''}
                            onChange={(e) => setSettings({ ...settings, featured_pillar_override_id: e.target.value })}
                            className="w-full"
                            data-testid="setting-featured-pillar"
                        >
                            <option value="">— Auto (monthly rotation) —</option>
                            {pillars.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.name}{p.specialty ? ` · ${p.specialty}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="border-t border-[#E5E0D8] pt-8">
                <h3 className="font-serif text-2xl text-[#1A1A2E] mb-2">Footer</h3>
                <p className="text-sm text-[#4A4A68] mb-6">Customize footer content, navigation links, and social links.</p>

                {FOOTER_FIELDS.map(f => (
                    <div key={f.key} className="mb-6">
                        <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">
                            {f.label}
                        </label>
                        {f.type === 'textarea' ? (
                            <textarea
                                rows={3}
                                value={settings[f.key] || ''}
                                onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                                className="w-full resize-none"
                                data-testid={`setting-${f.key}`}
                            />
                        ) : (
                            <input
                                type={f.type}
                                value={settings[f.key] || ''}
                                onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                                className="w-full"
                                data-testid={`setting-${f.key}`}
                            />
                        )}
                    </div>
                ))}

                <div className="mb-6">
                    <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-3">
                        Footer "Explore" Links
                    </label>
                    <LinkRepeater
                        items={settings.footer_links || []}
                        onChange={(arr) => setSettings({ ...settings, footer_links: arr })}
                        prefix="footer-link"
                    />
                </div>

                <div>
                    <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-3">
                        Footer "Connect" / Social Links
                    </label>
                    <LinkRepeater
                        items={settings.footer_social || []}
                        onChange={(arr) => setSettings({ ...settings, footer_social: arr })}
                        prefix="footer-social"
                    />
                </div>
            </div>

            <button
                type="submit"
                className="btn-primary flex items-center gap-2"
                disabled={saving}
                data-testid="save-settings-btn"
            >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : 'Save Settings'}
            </button>
        </form>
    );
}
